import { lazy, Suspense, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, NavLink, Route, Routes, useLocation } from "react-router-dom";
import { HomePage } from "./routes/HomePage";
import { MarketsPage } from "./routes/MarketsPage";
import { MarketPage } from "./routes/MarketPage";
import { useWalletAuth } from "./lib/walletAuth";
import { buildWalletHeaderOptions, DISCONNECT_WALLET_VALUE } from "./lib/walletHeaderOptions";
import { walletDebug } from "./lib/walletDebug.js";
import { initAudioPreferences, isMuted, toggleMute } from "./lib/audio";
import { captureAffiliateFromSearch } from "./lib/affiliate";
import { registerWebVitals } from "./lib/webVitals";
import { RouteSkeleton } from "./components/skeletons";
import { UserAvatar } from "./components/UserAvatar";
import { getMobileWalletBrowserMessage } from "./solana/walletSupport";
import { getInitialNotableEventsSnapshot } from "./lib/api";
import { TermsAcceptanceModal } from "./components/TermsAcceptanceModal";
import { useTermsAcceptance } from "./lib/termsAcceptanceContext.jsx";
import { NotableEventsMarquee } from "./components/NotableEventsMarquee";
import { serviceName, serviceXHandle, serviceXProfileUrl } from "../shared/brand.js";
import { audienceBenefitOrder, audienceBenefitPages } from "../shared/audienceBenefits.js";
import { gameTheoryPage } from "../shared/gameTheoryContent.js";
import { seasonZeroPage } from "../shared/seasonZeroContent.js";

const CreateMarketPage = lazy(() => import("./routes/CreateMarketPage").then((module) => ({ default: module.CreateMarketPage })));
const LeaderboardsPage = lazy(() => import("./routes/LeaderboardsPage").then((module) => ({ default: module.LeaderboardsPage })));
const ProfilePage = lazy(() => import("./routes/ProfilePage").then((module) => ({ default: module.ProfilePage })));
const FaqPage = lazy(() => import("./routes/FaqPage").then((module) => ({ default: module.FaqPage })));
const PrivacyPolicyPage = lazy(() =>
  import("./routes/PrivacyPolicyPage").then((module) => ({ default: module.PrivacyPolicyPage }))
);
const TermsPage = lazy(() => import("./routes/TermsPage").then((module) => ({ default: module.TermsPage })));
const AudienceBenefitsPage = lazy(() =>
  import("./routes/AudienceBenefitsPage").then((module) => ({ default: module.AudienceBenefitsPage }))
);
const GameTheoryPage = lazy(() => import("./routes/GameTheoryPage").then((module) => ({ default: module.GameTheoryPage })));
const SeasonZeroPage = lazy(() => import("./routes/SeasonZeroPage").then((module) => ({ default: module.SeasonZeroPage })));
const SolanaAppStorePage = lazy(() =>
  import("./routes/SolanaAppStorePage").then((module) => ({ default: module.SolanaAppStorePage }))
);
const NotableEventsAdminPage = lazy(() =>
  import("./routes/NotableEventsAdminPage").then((module) => ({ default: module.NotableEventsAdminPage }))
);
const SettlementAdminPage = lazy(() =>
  import("./routes/SettlementAdminPage").then((module) => ({ default: module.SettlementAdminPage }))
);
const PhantomCallbackPage = lazy(() =>
  import("./routes/PhantomCallbackPage").then((module) => ({ default: module.PhantomCallbackPage }))
);

const learnHeaderLinks = [
  { label: gameTheoryPage.navLabel, path: gameTheoryPage.path },
  { label: "FAQ", path: "/faq" },
  { label: "Privacy", path: "/privacy" },
  { label: "Terms", path: "/terms" }
];

export function App() {
  const [muted, setMuted] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [initialNotableEvents] = useState(() => getInitialNotableEventsSnapshot());
  const location = useLocation();
  const termsAcceptance = useTermsAcceptance();
  const year = new Date().getFullYear();
  const {
    authBusy,
    authError,
    canLogout,
    connectAndAuthenticate,
    connectors,
    disconnectAndLogout,
    isAuthenticated,
    loadWalletRuntime,
    session,
    status,
    walletCanSignTransaction,
    walletReady,
    walletRuntimeLoaded,
    walletRuntimeLoading,
    walletSupportMessage
  } = useWalletAuth();
  const userAgent = typeof window === "undefined" ? "" : window.navigator.userAgent;
  const mobileWalletBrowserMessage = getMobileWalletBrowserMessage({ connectors, userAgent });
  const walletHeaderSupportMessage =
    walletSupportMessage && mobileWalletBrowserMessage && walletSupportMessage !== mobileWalletBrowserMessage
      ? `${walletSupportMessage} ${mobileWalletBrowserMessage}`
      : walletSupportMessage || mobileWalletBrowserMessage;

  useEffect(() => {
    setMuted(initAudioPreferences());
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useLayoutEffect(() => {
    window.scrollTo({ left: 0, top: 0 });
  }, [location.pathname]);

  useEffect(() => {
    captureAffiliateFromSearch(location.search);
  }, [location.search]);

  useEffect(() => {
    registerWebVitals();
  }, []);

  return (
    <div className="theme-page flex flex-col">
      <header className="theme-header">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-4 px-4 py-4 lg:min-h-[5.5rem]">
          <div className="flex items-center gap-6">
            <Link to="/" className="theme-brand" aria-label={serviceName}>
              <img src="/favicon.svg" alt="" className="theme-brand-logo" />
            </Link>
          </div>

          <div className="hidden min-w-0 flex-1 justify-center lg:flex">
            <nav className="theme-nav">
              <NavTextLink to="/markets">Markets</NavTextLink>
              <NavTextLink to="/create">Create</NavTextLink>
              <NavTextLink to="/leaderboards">Leaderboards</NavTextLink>
              <AudienceHeaderDropdown pathname={location.pathname} />
              <NavTextLink to={seasonZeroPage.path}>{seasonZeroPage.navLabel}</NavTextLink>
              <LearnHeaderDropdown pathname={location.pathname} />
            </nav>
          </div>

          <div className="hidden shrink-0 items-center justify-end gap-2 lg:flex">
            <div data-cls-measure="app-header-wallet" className="min-h-11 w-44 min-w-0 shrink-0">
              <WalletHeaderControl
                authBusy={authBusy}
                connectAndAuthenticate={connectAndAuthenticate}
                connectors={connectors}
                disconnectAndLogout={disconnectAndLogout}
                isAuthenticated={isAuthenticated}
                canLogout={canLogout}
                loadWalletRuntime={loadWalletRuntime}
                session={session}
                status={status}
                walletCanSignTransaction={walletCanSignTransaction}
                walletReady={walletReady}
                walletRuntimeLoaded={walletRuntimeLoaded}
                walletRuntimeLoading={walletRuntimeLoading}
                walletSupportMessage={walletHeaderSupportMessage}
              />
            </div>
            <HeaderSoundButton muted={muted || isMuted()} onClick={() => setMuted(toggleMute())} />
            {isAuthenticated ? <ProfileAvatarLink session={session} /> : null}
          </div>

          <div className="ml-auto flex items-center gap-2 lg:hidden">
            {isAuthenticated ? <ProfileAvatarLink session={session} testId="mobile-header-profile-avatar" /> : null}
            <button
              type="button"
              onClick={() => setMenuOpen((previous) => !previous)}
              className="inline-flex items-center justify-center rounded-md border border-zinc-700 px-3 py-2 text-sm text-zinc-100 transition-colors hover:border-zinc-500"
              aria-expanded={menuOpen}
              aria-controls="mobile-header-menu"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
            >
              {menuOpen ? "Close" : "Menu"}
            </button>
          </div>
        </div>

        {authError ? (
          <div className="mx-auto hidden w-full max-w-6xl px-4 pb-2 text-right text-xs text-rose-300 lg:block">{authError}</div>
        ) : null}

        <NotableEventsMarquee initialEvents={initialNotableEvents} />

        {menuOpen ? (
          <div id="mobile-header-menu" className="border-t border-zinc-800/80 px-4 py-3 lg:hidden">
            <nav className="theme-nav-mobile">
              <NavTextLink to="/markets">Markets</NavTextLink>
              <NavTextLink to="/create">Create</NavTextLink>
              <NavTextLink to="/leaderboards">Leaderboards</NavTextLink>
              <AudienceHeaderDropdown pathname={location.pathname} mobile />
              <NavTextLink to={seasonZeroPage.path}>{seasonZeroPage.navLabel}</NavTextLink>
              <LearnHeaderDropdown pathname={location.pathname} mobile />
            </nav>
            <button
              type="button"
              onClick={() => setMuted(toggleMute())}
              className="mt-4 inline-flex min-h-11 rounded-full border border-zinc-700 px-4 py-2 text-sm text-zinc-100 transition-colors hover:border-zinc-500"
            >
              Sound: {muted || isMuted() ? "Muted" : "On"}
            </button>
            <div className="mt-3">
              <div data-cls-measure="app-header-wallet" className="min-h-11 min-w-36">
                <WalletHeaderControl
                  authBusy={authBusy}
                  connectAndAuthenticate={connectAndAuthenticate}
                  connectors={connectors}
                  disconnectAndLogout={disconnectAndLogout}
                  isAuthenticated={isAuthenticated}
                  canLogout={canLogout}
                  loadWalletRuntime={loadWalletRuntime}
                  session={session}
                  status={status}
                  walletCanSignTransaction={walletCanSignTransaction}
                  walletReady={walletReady}
                  walletRuntimeLoaded={walletRuntimeLoaded}
                  walletRuntimeLoading={walletRuntimeLoading}
                  walletSupportMessage={walletHeaderSupportMessage}
                />
              </div>
            </div>
            {isAuthenticated ? (
              <div className="mt-3 flex items-center gap-3 rounded-md border border-zinc-800 px-3 py-3">
                <ProfileAvatarLink session={session} testId="mobile-menu-profile-avatar" />
                <NavTextLink to="/profile">Profile</NavTextLink>
              </div>
            ) : null}
            {authError ? <p className="mt-2 text-xs text-rose-300">{authError}</p> : null}
          </div>
        ) : null}
      </header>
      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-6">
        <Suspense fallback={<RouteSkeleton variant={getRouteSkeletonVariant(location.pathname)} />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/markets" element={<MarketsPage />} />
            <Route path="/market/:marketId" element={<MarketPage />} />
            <Route path="/create" element={<CreateMarketPage />} />
            <Route path="/leaderboards" element={<LeaderboardsPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/players" element={<AudienceBenefitsPage audience="players" />} />
            <Route path="/creators" element={<AudienceBenefitsPage audience="creators" />} />
            <Route path="/influencers" element={<AudienceBenefitsPage audience="influencers" />} />
            <Route path="/collections" element={<AudienceBenefitsPage audience="collections" />} />
            <Route path="/game-theory" element={<GameTheoryPage />} />
            <Route path="/season-0" element={<SeasonZeroPage />} />
            <Route path="/solana_app_store" element={<SolanaAppStorePage />} />
            <Route path="/auth/phantom/callback" element={<PhantomCallbackPage />} />
            <Route path="/ops/:adminPath" element={<NotableEventsAdminPage />} />
            <Route path="/ops/:adminPath/settlements" element={<SettlementAdminPage />} />
          </Routes>
        </Suspense>
      </main>
      <footer className="theme-footer">
        <div className="mx-auto flex w-full max-w-6xl flex-col items-center justify-between gap-3 px-4 py-4 text-sm lg:flex-row lg:items-center">
          <p>Copyright {year}</p>
          <p>
            <a
              href={serviceXProfileUrl}
              target="_blank"
              rel="noreferrer"
              className="theme-link"
            >
              Follow @{serviceXHandle}
            </a>
          </p>
          <div className="flex items-center gap-3">
            <a
              href={serviceXProfileUrl}
              target="_blank"
              rel="noreferrer"
              aria-label={`${serviceName} on X`}
              className="theme-icon-link"
            >
              <XIcon />
            </a>
            <a
              href="https://raiziq.com/"
              target="_blank"
              rel="noreferrer"
              aria-label="Raiziq website"
              className="theme-icon-link"
            >
              <WebsiteIcon />
            </a>
          </div>
        </div>
      </footer>
      <TermsAcceptanceModal
        open={termsAcceptance.modalOpen}
        currentTerms={termsAcceptance.currentTerms}
        blocking={termsAcceptance.blocking}
        busy={termsAcceptance.busy}
        error={termsAcceptance.error}
        onAccept={() => termsAcceptance.acceptTerms({ acceptanceSurface: "modal", acceptedFromPath: location.pathname })}
        onClose={termsAcceptance.closeModal}
        onSignOut={disconnectAndLogout}
      />
    </div>
  );
}

function getRouteSkeletonVariant(pathname) {
  if (pathname === "/leaderboards") {
    return "leaderboards";
  }
  return "default";
}

function WalletHeaderControl({
  authBusy,
  connectAndAuthenticate,
  connectors,
  disconnectAndLogout,
  isAuthenticated,
  canLogout,
  loadWalletRuntime,
  session,
  status,
  walletCanSignTransaction,
  walletReady,
  walletRuntimeLoaded,
  walletRuntimeLoading,
  walletSupportMessage
}) {
  const [selectedConnectorId, setSelectedConnectorId] = useState("");
  const needsWalletReconnect = isAuthenticated && !walletCanSignTransaction;
  const walletOptions = buildWalletHeaderOptions({ connectors, canLogout, isAuthenticated, session, walletCanSignTransaction });
  const selectedValue = walletOptions.value || (needsWalletReconnect ? "" : selectedConnectorId);

  useEffect(() => {
    walletDebug("header.wallet_options", {
      isAuthenticated,
      canLogout,
      walletCanSignTransaction,
      needsWalletReconnect,
      selectedValue,
      optionValues: walletOptions.options.map((option) => option.value),
      sessionWalletAddress: session?.walletAddress || ""
    });
  }, [
    canLogout,
    isAuthenticated,
    needsWalletReconnect,
    selectedValue,
    session,
    walletCanSignTransaction,
    walletOptions.options
  ]);

  useEffect(() => {
    if (isAuthenticated || !connectors.length) {
      setSelectedConnectorId("");
      return;
    }
    if (selectedConnectorId && !connectors.some((connector) => connector.id === selectedConnectorId)) {
      setSelectedConnectorId("");
    }
  }, [connectors, isAuthenticated, selectedConnectorId, walletCanSignTransaction]);

  if (!walletRuntimeLoaded) {
    const buttonLabel = walletRuntimeLoading
      ? isAuthenticated
        ? "Restoring wallet..."
        : "Loading wallet options..."
      : needsWalletReconnect
        ? "Reconnect wallet"
        : "Select wallet";
    return (
      <div className="flex w-full min-w-0 flex-col items-start gap-2 text-xs text-zinc-400 md:items-end md:text-right">
        <div className="flex w-full min-w-0 items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => loadWalletRuntime().catch(() => null)}
            className="inline-flex min-h-11 w-full min-w-0 items-center justify-center truncate rounded-full border border-zinc-700 px-4 py-2 text-sm text-zinc-100 transition-colors hover:border-zinc-500 disabled:opacity-60"
            disabled={walletRuntimeLoading}
            aria-label="Select wallet"
          >
            {buttonLabel}
          </button>
        </div>
        {walletSupportMessage ? <span>{walletSupportMessage}</span> : null}
      </div>
    );
  }

  if (!walletReady) {
    return (
      <div className="flex w-full min-w-0 flex-col items-start gap-2 text-xs text-zinc-400 md:items-end md:text-right">
        <div className="flex w-full min-w-0 items-center justify-end gap-2">
          <span className="inline-flex min-h-11 w-full min-w-0 items-center truncate rounded-full border border-zinc-800 px-4 py-2 text-xs text-zinc-400">
            Loading wallet options...
          </span>
        </div>
        {walletSupportMessage ? <span>{walletSupportMessage}</span> : null}
      </div>
    );
  }

  if (!isAuthenticated && walletOptions.options.length === 0) {
    return (
      <div className="flex w-full min-w-0 flex-col items-start gap-2 text-xs text-zinc-400 md:items-end md:text-right">
        <div className="flex w-full min-w-0 items-center justify-end gap-2">
          <select
            value=""
            aria-label="Select wallet"
            disabled
            className="min-h-11 w-full min-w-0 truncate rounded-full border border-zinc-800 bg-zinc-900 px-4 py-2 text-sm text-zinc-500"
          >
            <option value="">No wallets found</option>
          </select>
        </div>
        {walletSupportMessage ? <span>{walletSupportMessage}</span> : null}
      </div>
    );
  }

  return (
    <div className="flex w-full min-w-0 flex-col items-start gap-2 text-xs text-zinc-400 md:items-end md:text-right">
      <div className="flex w-full min-w-0 items-center justify-end gap-2">
        <select
          value={selectedValue}
          onChange={(event) => {
            const next = event.target.value;
            const option = walletOptions.options.find((candidate) => candidate.value === next);
            walletDebug("header.wallet_select.change", {
              value: next,
              action: option?.action || "",
              label: option?.label || ""
            });

            if (option?.action === "disconnect" || next === DISCONNECT_WALLET_VALUE) {
              window.__RAIZIQ_PRIVY_USER_SELECTED = false;
              disconnectAndLogout().catch(() => null);
              return;
            }

            if (option?.action === "connect") {
              setSelectedConnectorId("");
              if (next && (next.toLowerCase().includes("privy") || (option?.label || "").toLowerCase().includes("privy"))) {
                // Explicit user selection of Privy connector (fixes #80: only then mount PrivyProvider and allow its background GETs to privy.raiziq.com)
                window.__RAIZIQ_PRIVY_USER_SELECTED = true;
              }
              connectAndAuthenticate(next).catch(() => null);
            }
          }}
          className="min-h-11 w-full min-w-0 truncate rounded-full border border-zinc-700 bg-zinc-900 px-4 py-2 text-sm text-zinc-100 transition-colors hover:border-zinc-500 disabled:opacity-60"
          aria-label="Select wallet"
          disabled={status === "connecting" || authBusy || (!isAuthenticated && walletOptions.options.length === 0)}
        >
          {walletOptions.placeholder ? (
            <option value="" disabled>
              {walletOptions.placeholder}
            </option>
          ) : null}
          {walletOptions.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      {walletSupportMessage ? <span>{walletSupportMessage}</span> : null}
    </div>
  );
}

function HeaderSoundButton({ muted, onClick }) {
  const label = `Sound: ${muted ? "Muted" : "On"}`;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-zinc-700 text-zinc-100 transition-colors hover:border-zinc-500 hover:text-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-zinc-950"
    >
      {muted ? <SoundMutedIcon /> : <SoundOnIcon />}
    </button>
  );
}

function ProfileAvatarLink({ session, testId }) {
  const walletAddress = session?.walletAddress || session?.userId || "";
  return (
    <Link
      to="/profile"
      aria-label="View profile"
      data-testid={testId}
      className="inline-flex h-11 w-11 items-center justify-center rounded-full transition-transform hover:scale-[1.03] focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-zinc-950"
    >
      <UserAvatar walletAddress={walletAddress} />
    </Link>
  );
}

function SoundOnIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden="true">
      <path d="M4.5 9.5v5h3.2l4.1 3.4V6.1L7.7 9.5H4.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M15.5 8.6a5.1 5.1 0 0 1 0 6.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M18.2 6a8.8 8.8 0 0 1 0 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function SoundMutedIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden="true">
      <path d="M4.5 9.5v5h3.2l4.1 3.4V6.1L7.7 9.5H4.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="m16.1 9.7 4.2 4.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="m20.3 9.7-4.2 4.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function NavTextLink({ to, children }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        ["theme-nav-link", isActive ? "theme-nav-link-active" : "theme-nav-link-idle"].join(" ")
      }
    >
      {children}
    </NavLink>
  );
}

function AudienceHeaderDropdown({ pathname, mobile = false }) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const selectedPath = getSelectedAudiencePath(pathname);
  const menuId = mobile ? "mobile-audience-benefits-menu" : "desktop-audience-benefits-menu";

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open || typeof document === "undefined") {
      return undefined;
    }

    function closeWhenClickingOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    }

    document.addEventListener("pointerdown", closeWhenClickingOutside);
    return () => document.removeEventListener("pointerdown", closeWhenClickingOutside);
  }, [open]);

  const triggerClassName = [
    "theme-nav-link",
    selectedPath ? "theme-nav-link-active" : "theme-nav-link-idle",
    "gap-2",
    mobile ? "w-full justify-between" : ""
  ].join(" ");

  return (
    <div
      ref={dropdownRef}
      className={["relative", mobile ? "w-full" : ""].join(" ")}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setOpen(false);
        }
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          setOpen(false);
        }
      }}
    >
      <button
        type="button"
        className={triggerClassName}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((previous) => !previous)}
      >
        <span>Benefits</span>
        <span
          aria-hidden="true"
          className={[
            "h-0 w-0 border-x-[4px] border-t-[5px] border-x-transparent transition-transform",
            open ? "rotate-180" : "",
            selectedPath ? "border-t-blue-300" : "border-t-zinc-300"
          ].join(" ")}
        />
      </button>
      {open ? (
        <div
          id={menuId}
          role="group"
          aria-label="Audience benefits"
          className={[
            "z-30 grid gap-1 rounded-xl border border-zinc-800 bg-zinc-950 p-2 shadow-2xl shadow-black/30",
            mobile ? "mt-2 w-full" : "absolute left-0 top-full mt-2 min-w-64"
          ].join(" ")}
        >
          {audienceBenefitOrder.map((slug) => {
            const page = audienceBenefitPages[slug];
            return (
              <NavLink
                key={slug}
                to={page.path}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  [
                    "rounded-lg px-3 py-2 text-sm font-semibold transition-colors",
                    isActive ? "bg-zinc-900 text-blue-300" : "text-zinc-100 hover:bg-zinc-900 hover:text-blue-200",
                    mobile ? "" : "whitespace-nowrap"
                  ].join(" ")
                }
              >
                {page.footerLabel}
              </NavLink>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function getSelectedAudiencePath(pathname = "") {
  const pathOnly = String(pathname || "").split("?")[0].split("#")[0];
  const page = audienceBenefitOrder
    .map((slug) => audienceBenefitPages[slug])
    .find((candidate) => candidate.path === pathOnly);
  return page?.path || "";
}

function LearnHeaderDropdown({ pathname, mobile = false }) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const selectedPath = getSelectedLearnPath(pathname);
  const menuId = mobile ? "mobile-learn-menu" : "desktop-learn-menu";

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open || typeof document === "undefined") {
      return undefined;
    }

    function closeWhenClickingOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    }

    document.addEventListener("pointerdown", closeWhenClickingOutside);
    return () => document.removeEventListener("pointerdown", closeWhenClickingOutside);
  }, [open]);

  const triggerClassName = [
    "theme-nav-link",
    selectedPath ? "theme-nav-link-active" : "theme-nav-link-idle",
    "gap-2",
    mobile ? "w-full justify-between" : ""
  ].join(" ");

  return (
    <div
      ref={dropdownRef}
      className={["relative", mobile ? "w-full" : ""].join(" ")}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setOpen(false);
        }
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          setOpen(false);
        }
      }}
    >
      <button
        type="button"
        className={triggerClassName}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((previous) => !previous)}
      >
        <span>Learn</span>
        <span
          aria-hidden="true"
          className={[
            "h-0 w-0 border-x-[4px] border-t-[5px] border-x-transparent transition-transform",
            open ? "rotate-180" : "",
            selectedPath ? "border-t-blue-300" : "border-t-zinc-300"
          ].join(" ")}
        />
      </button>
      {open ? (
        <div
          id={menuId}
          role="group"
          aria-label="Learn"
          className={[
            "z-30 grid gap-1 rounded-xl border border-zinc-800 bg-zinc-950 p-2 shadow-2xl shadow-black/30",
            mobile ? "mt-2 w-full" : "absolute right-0 top-full mt-2 min-w-44"
          ].join(" ")}
        >
          {learnHeaderLinks.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                [
                  "rounded-lg px-3 py-2 text-sm font-semibold transition-colors",
                  isActive ? "bg-zinc-900 text-blue-300" : "text-zinc-100 hover:bg-zinc-900 hover:text-blue-200",
                  mobile ? "" : "whitespace-nowrap"
                ].join(" ")
              }
            >
              {link.label}
            </NavLink>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function getSelectedLearnPath(pathname = "") {
  const pathOnly = String(pathname || "").split("?")[0].split("#")[0];
  return learnHeaderLinks.find((link) => link.path === pathOnly)?.path || "";
}

function XIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden="true">
      <path d="M18.901 1.153h3.68l-8.04 9.19 9.458 12.504h-7.406l-5.802-7.584-6.639 7.584H.47l8.598-9.826L0 1.154h7.594l5.245 6.932 6.062-6.933Z" />
    </svg>
  );
}

function WebsiteIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden="true">
      <path d="M12 2a10 10 0 1 0 10 10A10.012 10.012 0 0 0 12 2Zm6.93 9h-3.35a15.7 15.7 0 0 0-1.2-5.1A8.03 8.03 0 0 1 18.93 11ZM12 4.07c.86 1.13 1.91 3.47 2.2 6.93H9.8C10.09 7.54 11.14 5.2 12 4.07ZM4.07 13h3.35a15.7 15.7 0 0 0 1.2 5.1A8.03 8.03 0 0 1 4.07 13Zm3.35-2H4.07a8.03 8.03 0 0 1 4.55-5.1A15.7 15.7 0 0 0 7.42 11Zm1.39 2h6.38c-.29 3.46-1.34 5.8-2.2 6.93-.86-1.13-1.91-3.47-2.2-6.93Zm5.57 5.1a15.7 15.7 0 0 0 1.2-5.1h3.35a8.03 8.03 0 0 1-4.55 5.1Z" />
    </svg>
  );
}
